// Client tipado de `loan-config-business`: una función por operationId del OpenAPI público.
// Los endpoints privados (`?tenantId=`) son para otros lambdas y no se exponen aquí.
import type {
    ParameterResponse,
    ParametersByTypeResponse,
} from '@fiado/type-kit/bin/loanConfig/dtos/responses/ParameterResponse';
import type { ParameterAuditEntry } from '@fiado/type-kit/bin/loanConfig/dtos/responses/ParameterAuditEntry';
import type {
    ListRecentParameterChangesResponse,
    RecentParameterChange,
} from '@fiado/type-kit/bin/loanConfig/dtos/responses/ListRecentParameterChangesResponse';
import type {
    TemplateMessageCreateRequest,
    TemplateMessageListFiltersRequest,
    TemplateMessageListResponse,
    TemplateMessageResponse,
    TemplateMessageUpdateRequest,
} from '@fiado/type-kit/bin/messagesConnector/index';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_LOAN_CONFIG_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

export type { ParameterResponse, ParametersByTypeResponse };
export type {
    TemplateMessageCreateRequest,
    TemplateMessageListFiltersRequest,
    TemplateMessageListResponse,
    TemplateMessageResponse,
    TemplateMessageUpdateRequest,
};

/** Query key del listado: UNA sola, o invalidar tras guardar deja la otra sirviendo dato viejo. */
export const PARAMETERS_QUERY_KEY = ['loan-config', 'parameters'] as const;

export interface BulkUpdateItem {
    paramType: string;
    key: string;
    /** SIEMPRE string en el wire (el backend valida contra `valueType`). `null` = pendiente. */
    value: string | null;
    reason?: string;
}

export interface BulkUpdateResult {
    updated: number;
    failed: { paramType: string; key: string; reason: string }[];
}

/**
 * `publicListParameters` — GET /public/parameters. Todos los parámetros agrupados por categoría.
 * Alimenta la pantalla Configuración global. Pide `loan.parameter.read`.
 */
export async function listParameters(): Promise<ParametersByTypeResponse> {
    const res = await call<{ parameters: ParametersByTypeResponse }>('/public/parameters');
    return res.parameters;
}

/**
 * `publicBulkUpdateParameters` — POST /public/parameters/bulk-update. Guardado en lote, tolerante
 * a fallos parciales: los que fallan vuelven en `failed` con su motivo. Pide `loan.parameter.update`.
 */
export function bulkUpdateParameters(parameters: BulkUpdateItem[]): Promise<BulkUpdateResult> {
    return call<BulkUpdateResult>('/public/parameters/bulk-update', {
        method: 'POST',
        body: JSON.stringify({ parameters }),
    });
}

/**
 * `publicGetParametersByType` — GET /public/parameters/{paramType}. Los parámetros de UNA
 * categoría. Pide `loan.parameter.read`.
 */
export async function listParametersByType(paramType: string): Promise<ParameterResponse[]> {
    const res = await call<{ parameters: ParameterResponse[] }>(
        `/public/parameters/${encodeURIComponent(paramType)}`,
    );
    return res.parameters;
}

export type { ParameterAuditEntry, RecentParameterChange, ListRecentParameterChangesResponse };

/**
 * `publicListRecentParameterChanges` — GET /public/parameters/audit/recent. Cambios de todos los
 * parámetros en los últimos `days` días, más reciente primero. Pide `loan.parameter.audit`.
 */
export function listRecentParameterChanges(
    days: number,
    limit: number,
): Promise<ListRecentParameterChangesResponse> {
    return call<ListRecentParameterChangesResponse>(
        `/public/parameters/audit/recent?days=${String(days)}&limit=${String(limit)}`,
    );
}

/** Tope de filas del histórico que acepta el backend; por encima responde `VALIDATION_ERROR`. */
export const PARAMETER_AUDIT_MAX_LIMIT = 200;

/**
 * `publicGetParameterAudit` — GET /public/parameters/audit/{paramType}/{key}. Histórico de un
 * parámetro, más reciente primero. Sin cursor: `limit` (default 50, tope 200) es toda la paginación.
 */
export async function listParameterAudit(
    paramType: string,
    key: string,
    limit?: number,
): Promise<ParameterAuditEntry[]> {
    const query = limit === undefined ? '' : `?limit=${String(limit)}`;
    const res = await call<{ history: ParameterAuditEntry[] }>(
        `/public/parameters/audit/${encodeURIComponent(paramType)}/${encodeURIComponent(key)}${query}`,
    );
    return res.history;
}

/**
 * `publicUpdateParameter` — PUT /public/parameters/{paramType}/{key}. Guarda UN parámetro; cada
 * key tiene su propio `valueType` y su propia fila de auditoría.
 */
export function updateParameter(
    paramType: string,
    key: string,
    value: string | null,
    reason?: string,
): Promise<ParameterResponse> {
    return call<ParameterResponse>(
        `/public/parameters/${encodeURIComponent(paramType)}/${encodeURIComponent(key)}`,
        { method: 'PUT', body: JSON.stringify({ value, reason }) },
    );
}

// ── Plantillas de mensaje ────────────────────────────────────────────────────────────────────
// Proxy del CRUD de `messages-business`, cuya API es VPC-only. El `tenantId` NUNCA se manda: lo
// fija el scope RBAC del operador.

/**
 * Los filtros que viajan por query string. `tenantId` está en el DTO del type-kit pero el proxy lo
 * ignora: el tenant sale del scope RBAC del operador.
 */
export type MessageTemplateFilters = TemplateMessageListFiltersRequest;

function templateQuery(filters: MessageTemplateFilters): string {
    const params = new URLSearchParams();
    for (const key of Object.keys(filters)) {
        // El tipo colapsa las props opcionales, pero en runtime el `undefined` sí llega cuando el
        // caller omite un filtro.
        const value = (filters as Record<string, string | number | undefined>)[key];
        if (value === undefined || value === '') continue;
        params.set(key, String(value));
    }
    const query = params.toString();
    return query === '' ? '' : `?${query}`;
}

/**
 * `publicListMessageTemplates` — GET /public/message-templates. Una fila POR CANAL: una plantilla
 * con SMS y push son dos entradas. `cursor` es opaco y se pasa tal cual.
 */
export function listMessageTemplates(
    filters: MessageTemplateFilters = {},
): Promise<TemplateMessageListResponse> {
    return call<TemplateMessageListResponse>(`/public/message-templates${templateQuery(filters)}`);
}

/** `publicCreateMessageTemplate` — POST /public/message-templates. */
export function createMessageTemplate(
    body: TemplateMessageCreateRequest,
): Promise<TemplateMessageResponse> {
    return call<TemplateMessageResponse>('/public/message-templates', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** `publicGetMessageTemplate` — GET /public/message-templates/{id}. El `id` es opaco. */
export function getMessageTemplate(id: string): Promise<TemplateMessageResponse> {
    return call<TemplateMessageResponse>(`/public/message-templates/${encodeURIComponent(id)}`);
}

/** `publicUpdateMessageTemplate` — PUT /public/message-templates/{id}. Solo campos no-key. */
export function updateMessageTemplate(
    id: string,
    body: TemplateMessageUpdateRequest,
): Promise<TemplateMessageResponse> {
    return call<TemplateMessageResponse>(`/public/message-templates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicDeleteMessageTemplate` — DELETE /public/message-templates/{id}. */
export function deleteMessageTemplate(id: string): Promise<null> {
    return call<null>(`/public/message-templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
